import debug from 'debug';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { push } from 'connected-react-router';
import { format } from 'url';
import { withAssetTypesConfiguration } from '@mulesoft/exchange-react-hooks';
import { selectors as commonSelectors } from '~/domains/common';
import RootStoreContext from '~/contexts/RootStoreContext';
import {
  actions as searchActions,
  selectors as searchSelectors
} from '~/domains/search';
import { ANG_SIDEBAR_CONSISTENCY_ENABLED } from '~/domains/featureFlags/keys';
import { selectors as featureFlagsSelectors } from '~/domains/featureFlags';
import * as sessionSelectors from '~/portals/store/domains/session/selectors';
import ANGFilters from '~/utils/ang/ANGFilters';
import { shouldGetFacetsWithOrganizationId } from '~/utils/ang/query';
import { parseSearch } from '~/utils/location';
import { ANG_EXTERNAL_QUERY_PARAMS } from '~/utils/navigation/constants';
import * as objectsUtils from '~/utils/objects';
import ANGSearch from './ANGSearch';

const log = debug('exchange:ang-search:container');

const mapStateToProps = (state, { assetTypesConfiguration, location }) => {
  const query = parseSearch(location.search);
  const context = commonSelectors.context(state);
  const profile = sessionSelectors.profile(state);
  const isANGSidebarConsistencyEnabled = featureFlagsSelectors.featureFlag(
    state,
    ANG_SIDEBAR_CONSISTENCY_ENABLED
  );
  const rootOrganizations = searchSelectors.rootOrganizations(state);
  const filters = ANGFilters.buildFromQuery(
    query,
    assetTypesConfiguration,
    profile,
    isANGSidebarConsistencyEnabled,
    rootOrganizations
  );
  const parsedQuery = filters.getAsQuery();
  const organizationId = profile?.organization.id;
  const shouldIncludeOrganizationId = shouldGetFacetsWithOrganizationId(
    query,
    isANGSidebarConsistencyEnabled
  );
  const getANGConfig = (onFilterUpdate) =>
    searchSelectors.angConfig(state, {
      updateFilter: ANGFilters.buildUpdateFilter(
        parsedQuery,
        assetTypesConfiguration,
        context,
        onFilterUpdate,
        shouldIncludeOrganizationId ? organizationId : undefined
      ),
      assetTypesConfiguration,
      sort: query.sort,
      show: query.show,
      search: query.search
    });

  return {
    getANGConfig,
    filters,
    profile,
    isANGSidebarConsistencyEnabled,
    rootOrganizations
  };
};

const mapDispatchToProps = (dispatch, { location }) => ({
  onFilterUpdate: (facets) => {
    dispatch(searchActions.updateFacets(facets));
  },
  onSearch: (
    filters,
    profile,
    isANGSidebarConsistencyEnabled,
    rootOrganizations
  ) => {
    const query = parseSearch(location.search) || {};
    const { search } = filters.getFilters();
    const shouldSetSortingToRelevance = !!(
      isANGSidebarConsistencyEnabled &&
      !query.search &&
      search
    );

    const newQuery = {
      ...filters.getNewQuery(
        query.show,
        profile,
        isANGSidebarConsistencyEnabled,
        rootOrganizations
      ),
      ...objectsUtils.pick(query, ...ANG_EXTERNAL_QUERY_PARAMS),
      ...(shouldSetSortingToRelevance ? { sort: 'score' } : {})
    };
    const newURL = format({ pathname: location.pathname, query: newQuery });

    log('received filters', filters);

    if (newURL !== `${location.pathname}${location.search}`) {
      log('dispatching new query state', newQuery);
      dispatch(push(newURL));
    }
  }
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  getANGConfig: undefined,
  onSearch: (filters) =>
    dispatchProps.onSearch(
      filters,
      stateProps.profile,
      stateProps.isANGSidebarConsistencyEnabled,
      stateProps.rootOrganizations
    ),
  angConfig: stateProps.getANGConfig(dispatchProps.onFilterUpdate)
});

export default compose(
  withAssetTypesConfiguration,
  withRouter,
  connect(mapStateToProps, mapDispatchToProps, mergeProps, {
    context: RootStoreContext
  })
)(ANGSearch);
